/**
 * "Stop someone reading this", in a sheet: from an item or a compartment, choose the person, see the ways to
 * stop them with what each one changes counted beforehand, and do one. The three levers are the
 * model's own: lower their clearance, take them out of a compartment, or relabel the item.
 * Each change is audited by the API; afterwards the flow checks again and says whether it worked.
 */

import { mayRead, type Entitlement, type MemoryItem, type Sensitivity } from '@aioe/shared';
import { Layers, Lock, PersonXmark, Tag } from '@gravity-ui/icons';
import { useState, type ReactNode } from 'react';

import { problemText, routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { subjectName } from '../directory.ts';
import { Who } from './memoryAccess.tsx';
import { atLeast, compartmentName, holdsDirectly, itemName, LEVEL_TEXT, levelAbove, levelBelow, plural, usePeopleReach, withClearance, type AccessModel, type CompartmentRow } from './memoryAccessModel.ts';
import { SubjectName, SubjectPicker } from './SubjectPicker.tsx';
import { Button, ChoiceSelect, ErrorNote, inputClass, StatusMark } from './ui.tsx';
import { FormField, FormSheet } from './ui/FormSheet.tsx';
import { ChoiceCard } from './ui/ChoiceCard.tsx';
import { VIEW_TONES } from './ui/Art.tsx';

const tone = VIEW_TONES.memory!;

export type StopTarget = { item?: string; compartment?: string; person?: string };

interface Lever {
  id: string;
  icon: ReactNode;
  title: string;
  description: ReactNode;
  /** Why it cannot be used here, in words; the card shows it disabled. */
  unavailable?: string;
  needsReason?: boolean;
  run: (reason: string) => Promise<unknown>;
}

export function StopReading({ model, target, reload, onClose }: { model: AccessModel; target: StopTarget; reload: () => void; onClose: () => void }) {
  const item = target.item ? model.items.find((i) => i.id === target.item) : undefined;
  const compartment = target.compartment ? model.compartments.find((c) => c.id === target.compartment) : undefined;
  const people = usePeopleReach(model);
  const [person, setPerson] = useState<string | undefined>(target.person);
  const e = useLoad(() => (person ? routes.orgEntitlement(person) : Promise.resolve(undefined)), [person, model.clearances, model.compartments]);
  const [chosen, setChosen] = useState<string>();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<string>();
  const what = item ? itemName(item) : (compartment?.name ?? '');
  if (!item && !compartment) return null;

  // Who reads it now, among the people the directory names: the ones to choose from.
  const readers = [...(people.data ?? [])].filter(([, pe]) => (item ? mayRead(pe, item.label) : pe.compartments.includes(compartment!.id))).map(([id]) => id);
  const levers = person && e.data ? (item ? itemLevers(model, item, person, e.data, people.data) : compartmentLevers(model, compartment!, person, e.data)) : [];
  const lever = levers.find((l) => l.id === chosen);
  const readsNow = person && e.data ? (item ? mayRead(e.data, item.label) : e.data.compartments.includes(compartment!.id)) : undefined;

  const doIt = async () => {
    if (!lever) return;
    setBusy(true);
    setError(undefined);
    try {
      await lever.run(reason.trim());
      reload();
      const after = await routes.orgEntitlement(person!);
      const still = item ? mayRead(after, { ...item.label, ...(lever.id.startsWith('relabel') ? relabelOf(lever.id, item) : {}) }) : after.compartments.includes(compartment!.id);
      setDone(still ? 'still' : 'stopped');
    } catch (err) {
      setError(problemText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormSheet
      width="xl"
      title={`Stop someone reading ${what}`}
      description="Choose the person, then one of the ways to stop them; what each one changes is counted first."
      onClose={onClose}
      footer={
        done ? (
          <Button size="sm" variant="primary" onPress={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button size="sm" variant="tertiary" onPress={onClose}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" isPending={busy} isDisabled={!lever || (lever.needsReason && !reason.trim())} onPress={() => void doIt()}>
              {lever?.title ?? 'Stop them reading it'}
            </Button>
          </>
        )
      }
    >
      {!done && (
        <Step n={1} title="Who should stop reading it?">
          {readers.length > 0 && (
            <ChoiceSelect
              label="A person who reads it"
              placeholder="A person who reads it"
              className="w-full"
              value={person && readers.includes(person) ? person : undefined}
              options={Object.fromEntries(readers.map((id) => [id, nameFor(id)]))}
              onChange={(id) => (setPerson(id), setChosen(undefined), setDone(undefined))}
            />
          )}
          <SubjectPicker kinds={['user']} placeholder={readers.length ? 'Or someone else' : 'Find a person'} onPick={(s) => (setPerson(s.id), setChosen(undefined), setDone(undefined))} />
          {person && readsNow === false && (
            <p className="text-sm text-muted">
              <SubjectName id={person} /> does not read it now: nothing to stop.
            </p>
          )}
        </Step>
      )}
      {person && readsNow && !done && (
        <Step n={2} title="Choose how">
          <div className="grid grid-cols-[minmax(0,1fr)] gap-3">
            {levers.map((l) => (
              <ChoiceCard
                key={l.id}
                testId={l.id}
                tone={tone}
                selected={chosen === l.id}
                disabled={Boolean(l.unavailable)}
                onPress={() => setChosen(l.id)}
                icon={l.icon}
                title={l.title}
                description={l.unavailable ?? l.description}
                badge={l.unavailable ? <StatusMark state="disabled" label="Not possible here" /> : undefined}
              />
            ))}
          </div>
        </Step>
      )}
      {lever && !done && lever.needsReason ? (
        <Step n={3} title="Say why">
          <FormField label="Why" hint="Kept in the audit trail.">
            <input className={inputClass} value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder="Leaving the merger team" />
          </FormField>
        </Step>
      ) : null}
      <ErrorNote error={error} />
      {done && person && (done === 'stopped' ? <Outcome state="connected">Done: {nameFor(person)} no longer reads {what}.</Outcome> : <Outcome state="error">{nameFor(person)} still reads {what}: they hold it another way, through a group or a project.</Outcome>)}
    </FormSheet>
  );
}

/** A dot and a sentence that wraps: the outcome of the flow. */
function Outcome({ state, children }: { state: 'connected' | 'error'; children: ReactNode }) {
  return (
    <p className="flex items-start gap-1.5 text-sm break-words">
      <span className="status-dot mt-1.5 shrink-0" data-state={state} aria-hidden="true" />
      <span className="min-w-0">{children}</span>
    </p>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <section className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-2">
      <h3 className="flex items-center gap-2 text-sm font-medium">
        <span className="inline-flex size-5 items-center justify-center rounded-full bg-surface-secondary text-xs tabular-nums" aria-hidden="true">
          {n}
        </span>
        {title}
      </h3>
      {children}
    </section>
  );
}

/** A person's display name for a text-only place (a select option): the directory's, else the id. */
function nameFor(id: string): string {
  return subjectName(id)?.name ?? id;
}

/** The label a relabel lever sets, from its id (`relabel:level:<l>` or `relabel:compartment:<id>`). */
function relabelOf(id: string, item: MemoryItem): { sensitivity: Sensitivity; compartments: string[] } {
  const [, kind, value] = id.split(':', 3) as [string, string, string];
  const rest = id.slice(`relabel:${kind}:`.length) || value;
  return kind === 'level' ? { sensitivity: rest as Sensitivity, compartments: item.label.compartments } : { sensitivity: item.label.sensitivity, compartments: [...new Set([...item.label.compartments, rest])] };
}

/** The three levers for an item, each with what it changes counted. */
function itemLevers(m: AccessModel, item: MemoryItem, person: string, e: Entitlement, people: Map<string, Entitlement> | undefined): Lever[] {
  const others = (pred: (x: MemoryItem) => boolean) => m.items.filter((x) => x.id !== item.id && pred(x)).length;
  const out: Lever[] = [];

  // 1. Lower their clearance to just below the item's level.
  const to = levelBelow(item.label.sensitivity);
  out.push({
    id: 'clearance',
    icon: <Layers />,
    title: to ? `Lower them to ${LEVEL_TEXT[to]}` : 'Lower their clearance',
    description: to ? `They read up to ${LEVEL_TEXT[to]} from now on, and also lose ${plural(others((x) => mayRead(e, x.label) && !mayRead({ ...e, clearance: to }, x.label)), 'other item')}.` : '',
    unavailable: to ? undefined : 'It is Public: everyone reads it whatever their clearance. Relabel it instead.',
    run: () => routes.setClearances(withClearance(m.clearances, { type: 'user', id: person }, to)),
  });

  // 2. Take them out of a compartment the item is in.
  const direct = item.label.compartments.find((c) => holdsDirectly(m, person, c));
  const viaElse = item.label.compartments.find((c) => e.compartments.includes(c));
  out.push({
    id: 'compartment',
    icon: <Lock />,
    title: direct ? `Take them out of ${compartmentName(m, direct)}` : 'Take them out of a compartment',
    description: direct ? `They also lose ${plural(others((x) => x.label.compartments.includes(direct) && mayRead(e, x.label)), 'other item')} in ${compartmentName(m, direct)}.` : '',
    unavailable: direct
      ? undefined
      : item.label.compartments.length === 0
        ? 'It is in no compartment. Relabel it into one instead.'
        : `They hold ${compartmentName(m, viaElse ?? item.label.compartments[0]!)} through a group or a project's members. Change that on the compartment's card.`,
    run: async () => {
      const c = m.compartments.find((x) => x.id === direct)!;
      await routes.setCompartmentGrants(c.id, c.grants.filter((g) => !(g.subjectType === 'user' && g.subjectId === person)));
    },
  });

  // 3. Relabel the item: raise it above their clearance, or put it in a compartment they do not hold.
  const mine = m.me;
  const up = levelAbove(e.clearance);
  const canRaise = up && mine && atLeast(mine.clearance, up);
  const intoCompartment = mine?.compartments.find((c) => !e.compartments.includes(c) && !item.label.compartments.includes(c));
  const losers = (label: { sensitivity: Sensitivity; compartments: string[] }) => [...(people ?? [])].filter(([id, pe]) => id !== person && mayRead(pe, item.label) && !mayRead(pe, label)).length;
  const groupsBelow = (l: Sensitivity) => m.clearances.filter((c) => c.subjectType === 'group' && atLeast(c.level, item.label.sensitivity) && !atLeast(c.level, l)).length;
  if (canRaise) {
    const label = { sensitivity: up, compartments: item.label.compartments };
    const n = losers(label);
    out.push({
      id: `relabel:level:${up}`,
      icon: <Tag />,
      title: `Raise it to ${LEVEL_TEXT[up]}`,
      description: `Everyone cleared below ${LEVEL_TEXT[up]} loses it: they${n ? `, ${plural(n, 'other person', 'other people')} named here` : ''}${groupsBelow(up) ? ` and ${plural(groupsBelow(up), 'group')}` : ''}.`,
      needsReason: true,
      run: (reason) => routes.relabelOrgMemory(item.id, { label, reason }),
    });
  } else if (intoCompartment) {
    const label = { sensitivity: item.label.sensitivity, compartments: [...item.label.compartments, intoCompartment] };
    const n = losers(label);
    const holders = m.compartments.find((c) => c.id === intoCompartment)?.grants.length ?? 0;
    out.push({
      id: `relabel:compartment:${intoCompartment}`,
      icon: <Tag />,
      title: `Put it in ${compartmentName(m, intoCompartment)}`,
      description: `${plural(holders, 'holder')} keep it. They${n ? ` and ${plural(n, 'other person', 'other people')} named here` : ''} lose it.`,
      needsReason: true,
      run: (reason) => routes.relabelOrgMemory(item.id, { label, reason }),
    });
  } else {
    out.push({ id: 'relabel', icon: <Tag />, title: 'Relabel the item', description: '', unavailable: 'You can label it only within your own reach: they read everything you do. Raise your own clearance, or hold a compartment they do not.', run: async () => undefined });
  }
  return out;
}

/** For a compartment: take the person out, or the group or project they hold it through. */
function compartmentLevers(m: AccessModel, c: CompartmentRow, person: string, e: Entitlement): Lever[] {
  const carrying = m.items.filter((x) => x.label.compartments.includes(c.id) && mayRead(e, x.label)).length;
  const without = (drop: (g: CompartmentRow['grants'][number]) => boolean) => () => routes.setCompartmentGrants(c.id, c.grants.filter((g) => !drop(g)));
  const out: Lever[] = [];
  const direct = holdsDirectly(m, person, c.id);
  out.push({
    id: 'person',
    icon: <PersonXmark />,
    title: 'Take them out',
    description: `They lose the ${plural(carrying, 'item')} in ${c.name} they read now, unless they hold it another way.`,
    unavailable: direct ? undefined : `${c.name} is not granted to them by name: they hold it through a group or a project's members.`,
    run: without((g) => g.subjectType === 'user' && g.subjectId === person),
  });
  for (const g of c.grants.filter((x) => x.subjectType !== 'user').slice(0, 2)) {
    out.push({
      id: `${g.subjectType}:${g.subjectId}`,
      icon: <Lock />,
      title: g.subjectType === 'group' ? 'Take their group out' : "Take the project's members out",
      description: (
        <>
          If they hold it as one of <Who type={g.subjectType} id={g.subjectId} projects={m.projects} />, this stops them, and everyone else who holds it only that way.
        </>
      ),
      run: without((x) => x.subjectType === g.subjectType && x.subjectId === g.subjectId),
    });
  }
  return out;
}
